<template>
  <div>
    <v-overlay v-model="savingAddress" z-index="2">
      <v-progress-linear color="#7C40DC" indeterminate class="mb-4"></v-progress-linear>
    </v-overlay>
    <!-- Display a payment form! -->
    <form id="payment-form">
      <div v-show="!addressSaved">
        <div class="d-flex d-flex flex-column">
          <span class="text-h5 mb-1">Shipping Address</span>
        </div>
        <div id="address-element" class="pb-10"></div>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="auto">
                  <v-btn
                    color="#F4F2F7"
                    rounded
                    class="sigfloButtonSmall text-capitalize"
                    @click="$emit('previous-step')"
                    ><v-icon color="" class="mr-2">mdi-arrow-left-circle-outline </v-icon>Back to Order Summary</v-btn
                  >
                </v-col>
                <v-col cols="auto">
                  <v-btn rounded color="#7C40DC" dark @click="saveAddress" class="sigfloButtonSmall text-capitalize">
                    Save Address and Checkout<v-icon color="" class="ml-2"
                      >mdi-arrow-right-circle-outline
                    </v-icon></v-btn
                  ></v-col
                >
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <!--Stripe.js injects the Payment Element-->
      </div>
      <div v-show="addressSaved" class="">
        <div class="">
          <span class="text-body1 mb-1">Shipping To:</span>
          <span class="text-body1 ml-4 mb-1 primary--text">{{ address }}</span>
          <v-btn x-small class="ml-4" color="primary" :loading="savingAddress" @click="addressSaved = false"
            >Change</v-btn
          >
        </div>
        <v-divider class="my-1" />
        <div class="d-flex d-flex flex-column pt-8">
          <span class="text-h5 mb-1">Payment Information</span>
        </div>
        <div id="payment-element">
          <!--Stripe.js injects the Payment Element-->
        </div>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-row justify="space-between">
                <v-col cols="auto">
                  <v-btn @click="addressSaved = false" dark class=""
                    ><v-icon color="white" class="mr-2">mdi-arrow-left-circle-outline </v-icon>Back to Address</v-btn
                  >
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    dark
                    @click="submitPayment"
                    id="submit"
                    :loading="savingPayment"
                    color="green lighten-2"
                    class="mt-2"
                    ><v-icon class="mr-2">mdi-check-bold</v-icon> Pay now
                  </v-btn></v-col
                >
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <div id="payment-message" class="hidden"></div>
      </div>
    </form>
    <!-- address error -->
    <div v-show="addressMessage" class="error--text">{{ addressMessage }}</div>
    <!-- payment error -->
    <div v-show="paymentMessage" class="error--text">
      <v-alert border="right" colored-border type="error" elevation="2">
        {{ paymentMessage }}
      </v-alert>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';

  export default {
    components: {},
    data() {
      return {
        elements: null,
        paymentMessage: null,
        addressMessage: null,
        addressSaved: false,
        address: null,
        addressElement: null,
        paymentElement: null,
        savingAddress: false,
        savingPayment: false
      };
    },
    watch: {
      'checkout.clientSecret': function (newVal) {
        if (newVal) {
          this.updateCheckout();
        }
      }
    },
    computed: {
      ...mapState(['configurator', 'checkout']),
      cartTotal() {
        const projectTotal = this.project ? this.project.sellPrice : 0;
        const shippingTotal = this.checkout.shippingTotal;
        const taxTotal = this.checkout.taxTotal;
        return projectTotal + shippingTotal + taxTotal;
      }
    },
    methods: {
      /**
       * Saves the address entered by the user.
       *
       * This function extracts the potentially complete address from the address element,
       * calculates the tax based on the address, saves the address to the checkout store,
       * and updates the address display and status flags accordingly.
       *
       * @returns {void}
       */
      saveAddress() {
        this.addressMessage = '';
        // Extract potentially complete address
        this.addressElement.getValue().then((result) => {
          if (!result.complete) return;
          const address = result.value.address;

          // save address
          this.savingAddress = true;
          this.$store
            .dispatch('checkout/saveAddress', address)
            .then(() => {
              this.address =
                address.line1 +
                ', ' +
                (address.line2 ? address.line2 + ', ' : '') +
                address.city +
                ', ' +
                address.state +
                ', ' +
                address.postal_code;

              // Calculate tax
              // NOTE: Don't calculate tax until address is saved
              this.$store
                .dispatch('checkout/calculateTax', result.value)
                .then((taxResult) => {
                  this.$store.commit('checkout/setTaxTotal', taxResult.result);
                  this.addressSaved = true;
                  // update payment intent
                  this.$store
                    .dispatch('checkout/updatePaymentIntent', {
                      projectId: this.checkout.projectId,
                      paymentIntentId: this.checkout.paymentIntentId
                    })
                    .then((response) => {
                      this.$store.commit('checkout/setClientSecret', response.clientSecret);
                    });
                })
                .catch((ex) => {
                  console.log(ex.toString());
                  this.addressMessage = ex.toString();
                  this.address = null;
                  this.addressSaved = false;
                  //this.$store.dispatch('app/showError', this.addressMessage);
                })
                .finally(() => {
                  this.savingAddress = false;
                });
            })
            .catch((ex) => {
              console.log(ex.toString());
              this.savingAddress = false;
              this.addressMessage = ex.toString();
              this.address = null;
              this.addressSaved = false;
              //this.$store.dispatch('app/showError', this.addressMessage);
            });
        });
      },
      /**
       * Updates the checkout process by initializing and mounting the Stripe elements.
       * This method creates and mounts the address and payment elements, and handles the change events.
       * When the address element is complete, it extracts the address, calculates the tax, saves the address, and updates the address display.
       * The payment element is mounted with tab layout.
       */
      updateCheckout() {
        const appearance = {
          theme: 'stripe',
          variables: {
            colorPrimary: '#200c40'
          }
        };
        const clientSecret = this.checkout.clientSecret;
        this.elements = this.$stripe.elements({ appearance, clientSecret });
        // Address Element
        const addressElementOptions = { mode: 'shipping', defaultValues: {} };
        this.addressElement = this.elements.create('address', addressElementOptions);
        this.addressElement.mount('#address-element');

        // Payment Element
        const paymentElementOptions = {
          layout: 'tabs'
        };
        this.paymentElement = this.elements.create('payment', paymentElementOptions);
        this.paymentElement.mount('#payment-element');
        console.log('debug');
      },
      /**
       * Submits the payment using Stripe.
       *
       * @async
       * @function submitPayment
       * @returns {Promise<void>}
       */
      async submitPayment() {
        this.savingPayment = true;
        this.paymentMessage = null;

        const { paymentIntent, error } = await this.$stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            // Make sure to change this to your payment completion page
          },
          redirect: 'if_required'
        });

        if ((error && error.type === 'card_error') || (error && error.type === 'validation_error')) {
          this.paymentMessage = error.message;
        } else if (error) {
          this.paymentMessage = 'An unexpected error occurred.';
        } else if (paymentIntent.status == 'succeeded') {
          this.$store.dispatch('checkout/convertOrder').then((response) => {
            this.savingPayment = false;
            this.$store.commit('order/setCurrentOrder', response);
            // refresh order list
            this.$store.dispatch('order/getOrderList');
            // fire gtag event
            // eslint-disable-next-line
            gtag('event', 'purchase', {
              transaction_id: response.id,
              value: response.orderTotal,
              tax: response.orderTax,
              shipping: response.orderShipping,
              currency: 'USD',
              coupon: null
            });

            // handle successful payment
            this.navToOrderSuccessful(response.id);
          });
        }
      }
    }
  };
</script>
